import { InvestorsTable } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Investors/InvestorsTable/InvestorsTable';
import { NewLeaversModal } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Modals/NewLeaversModal/NewLeaversModal';
import { SendDocumentsModal } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Modals/SendDocumentsModal/SendDocumentsModal';
import { SendInvitationModal } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Modals/SendInvitationModal/SendInvitationModal';
import { RequestKycModal } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Modals/RequestKycModal/RequestKycModal';
import { Suspense } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { NewJoinersAndAllocationsModal as UpsertInvestorSharesModal } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Modals/NewJoinersAndAllocationsModal/NewJoinersAndAllocationsModal';
import { useManageInvestorsController } from './useManageInvestorsController';
import { MIPManageInvestorsV2Context } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Investors/MIPManageInvestorsV2Context';
import { useSendInvestorInvitations } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Investors/hooks/useSendInvestorInvitations.ts';
import { ProgressBar } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Investors/ProgressBar/ProgressBar';
import { showToastV2 } from '@dealstack/frontend/shared/mechanism/toast/useToastV2';
import { useTranslationMipManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { postMipRequestKyc } from '@dealstack/frontend/shared/dataExchange/services/mip-manage.service';
import { RequestKycDto } from '@dealstack/shared/lib/generated';
import { useRequiredParams } from '@dealstack/frontend/shared/utils/useRequiredParams';
import {
	MIP_MANAGE_ROUTES,
	PROGRAMME_SETTINGS_ROUTES,
} from '@dealstack/frontend/app/pages/Routing/routes.ts';
import { mipManagePath } from '@dealstack/frontend/app/pages/Routing/routes.utils.ts';

export const Investors = () => {
	const manageInvestorsControllerV2 = useManageInvestorsController();

	const tMipManage = useTranslationMipManage();
	const { sendInvitations: onSendInvestorInvitations } = useSendInvestorInvitations();
	const { dealId, mipId } = useRequiredParams('dealId', 'mipId');
	const location = useLocation();
	const navigate = useNavigate();
	const sendDocumentsSelectedInvestorsIds = manageInvestorsControllerV2.selectedInvestors.map(
		(investor) => investor.id
	);

	// Round trip to Master Documents: it opens the create drawer on `create=1` and, on
	// close, navigates back to `returnTo` with the stashed selection so this modal reopens.
	const goToCreateMasterDocument = () => {
		const masterDocumentsPath = mipManagePath(
			`${MIP_MANAGE_ROUTES.SETTINGS}${PROGRAMME_SETTINGS_ROUTES.DOCUMENTS}`,
			{ dealId, mipId }
		);
		navigate(`${masterDocumentsPath}?create=1`, {
			state: {
				returnTo: `${location.pathname}${location.search}`,
				sendDocumentsSelection: { selectedIds: sendDocumentsSelectedInvestorsIds },
			},
		});
	};

	const onCloseSendDocumentModal = () => {
		manageInvestorsControllerV2.setShowSendDocumentModal(false);
		manageInvestorsControllerV2.setBulkActionPayload(null);
	};

	const onRequestKyc = async (payload: RequestKycDto): Promise<void> => {
		const response = await postMipRequestKyc(payload);
		const { sentCount = 0, skippedCount = 0 } = response?.payload ?? {};
		if (skippedCount && sentCount) {
			showToastV2({
				title: tMipManage('kyc_request_modal.partial_toast', {
					sent: sentCount,
					skipped: skippedCount,
				}),
				state: 'warning',
			});
		} else if (skippedCount) {
			showToastV2({
				title: tMipManage('kyc_request_modal.skipped_toast', { count: skippedCount }),
				state: 'warning',
			});
		} else {
			showToastV2({
				title: tMipManage('kyc_request_modal.success_toast'),
				state: 'success',
			});
		}
	};

	const onModalClose = async () => {
		await manageInvestorsControllerV2.getMIPInvestorsV2DataRequest.refetch();
	};

	const isLoading =
		manageInvestorsControllerV2.postMIPInvestorsInvitationsRequest.isLoading ||
		((manageInvestorsControllerV2.selectedInvestorIdsState.length > 0 ||
			!!manageInvestorsControllerV2.singleInvestorId) &&
			(manageInvestorsControllerV2.getMIPSelectedInvestorsDataRequest.isLoading ||
				manageInvestorsControllerV2.getMIPInvestorsDataForSendModalRequest.isLoading));

	return (
		<MIPManageInvestorsV2Context.Provider value={manageInvestorsControllerV2}>
			<InvestorsTable
				investors={manageInvestorsControllerV2.investors}
				customDetailColumns={manageInvestorsControllerV2.customDetailColumns}
				isLoading={manageInvestorsControllerV2.isLoading}
				stats={manageInvestorsControllerV2.stats}
				totalRecord={manageInvestorsControllerV2.totalInvestorsNumber}
				dataOperations={manageInvestorsControllerV2.mantineTableDataOperations}
				investorFilterOptions={manageInvestorsControllerV2.investorFilterOptions}
				investorFilterAsync={manageInvestorsControllerV2.investorFilterAsync}
				pivFilterOptions={manageInvestorsControllerV2.pivFilterOptions}
				pivFilterAsync={manageInvestorsControllerV2.pivFilterAsync}
				mipClassifierFilterOptions={manageInvestorsControllerV2.mipClassifierFilterOptions}
			/>

			<ProgressBar refreshInvestors={manageInvestorsControllerV2.refreshData} />

			{manageInvestorsControllerV2.isSendInvitationModalOpened && (
				<SendInvitationModal
					key={manageInvestorsControllerV2.investorsToSendInvitation.length}
					onClose={async () => {
						manageInvestorsControllerV2.setSingleInvestorId(null);
						manageInvestorsControllerV2.setIsSendInvitationModalOpened(false);
						await onModalClose();
					}}
					sendInvitation={onSendInvestorInvitations}
					isLoading={isLoading}
					investors={manageInvestorsControllerV2.investorsToSendInvitation}
				/>
			)}
			{/*
			 * Local Suspense boundary so the MIP_CREATE namespace fetch (read by the
			 * Joiners/Allocations and Leavers modals) doesn't bubble to the app-level
			 * <Suspense fallback={<FullscreenLoader />}> and swap the page out. Once the
			 * modals stop using MIP_CREATE this wrapper can be removed.
			 */}
			<Suspense fallback={null}>
				{manageInvestorsControllerV2.showNewJoinersAndAllocationsModal && (
					<UpsertInvestorSharesModal />
				)}
				{manageInvestorsControllerV2.showNewLeaversModal && <NewLeaversModal />}
				{manageInvestorsControllerV2.showSendDocumentModal && (
					<SendDocumentsModal
						selectedInvestorsIds={sendDocumentsSelectedInvestorsIds}
						bulkActionPayload={
							manageInvestorsControllerV2.bulkActionPayload ?? undefined
						}
						totalRecipients={manageInvestorsControllerV2.resolvedRecipientsCount}
						onClose={onCloseSendDocumentModal}
						onUploadDocument={goToCreateMasterDocument}
					/>
				)}
			</Suspense>
			{manageInvestorsControllerV2.isRequestKycModalOpen && (
				<RequestKycModal
					investorIds={manageInvestorsControllerV2.selectedInvestorIdsState}
					bulkActionPayload={manageInvestorsControllerV2.bulkActionPayload ?? undefined}
					totalRecipients={manageInvestorsControllerV2.resolvedRecipientsCount}
					onClose={() => {
						manageInvestorsControllerV2.setIsRequestKycModalOpen(false);
						manageInvestorsControllerV2.setBulkActionPayload(null);
					}}
					sendKycRequest={onRequestKyc}
				/>
			)}
		</MIPManageInvestorsV2Context.Provider>
	);
};
